/**
 * TypeScript interfaces for the Workflow Editor
 * Based on @xyflow/react types with custom extensions
 *
 * Node Types (matching backend GraphTraverserWorkflow):
 * - start: Entry point
 * - end: Exit point
 * - system: Fixed infrastructure nodes (init, retrieval, reflection)
 * - condition: Read-only branching node (needs_kg_search)
 * - orchestrator: Prompt-editable workflow steps (query-understanding, direct-response, synthesis)
 * - agent: Prompt-editable sub-agents (similarity, reference, structure, image, table)
 */

import type { Node, Edge, Viewport, OnNodesChange, OnEdgesChange, OnConnect } from '@xyflow/react';

// ===== Node Data Types =====
export interface BaseNodeData {
  label: string;
  isConfigured: boolean;
  // Index signature for React Flow Node<T> compatibility (requires Record<string, unknown>)
  [key: string]: unknown;
}

// ---------- System Nodes (fixed, no prompt) ----------
export interface StartNodeData extends BaseNodeData {
  type: 'start';
}

export interface EndNodeData extends BaseNodeData {
  type: 'end';
  outputFormat?: 'text' | 'json' | 'markdown';
}

export type SystemNodeType = 'init' | 'retrieval' | 'reflection';

export interface SystemNodeData extends BaseNodeData {
  type: 'system';
  nodeType: SystemNodeType;
  description?: string;
}

// ---------- Condition Node (read-only branching) ----------
export interface ConditionNodeData extends BaseNodeData {
  type: 'condition';
  description?: string;
  isReadOnly?: boolean; // Always true for needs_kg_search
  branches?: {
    true: string; // Target node ID for true branch
    false: string; // Target node ID for false branch
  };
}

// ---------- Orchestrator Nodes (prompt editable) ----------
export type OrchestratorNodeType = 'query_understanding' | 'direct_response' | 'synthesis';

export interface OrchestratorNodeData extends BaseNodeData {
  type: 'orchestrator';
  nodeType: OrchestratorNodeType;
  systemMessage?: string; // The prompt for this step
}

// ---------- Agent Nodes (5 sub-agents, prompt editable) ----------
export type AgentType = 'similarity' | 'reference' | 'structure' | 'image' | 'table' | 'custom' | 'direct_qa';

export interface AgentNodeData extends BaseNodeData {
  type: 'agent';
  agentType: AgentType;
  systemMessage?: string; // The prompt for this agent
  tools?: string[];
}

export interface ToolNodeData extends BaseNodeData {
  type: 'tool';
  toolId: string;
  toolType: string;
  config?: Record<string, unknown>;
}

// Union of all node data types
export type WorkflowNodeData =
  | StartNodeData
  | EndNodeData
  | SystemNodeData
  | ConditionNodeData
  | OrchestratorNodeData
  | AgentNodeData
  | ToolNodeData;

// ===== Custom Node Types =====
export type WorkflowNode = Node<WorkflowNodeData>;
export type WorkflowEdge = Edge;

// ===== Workflow Metadata =====
// NOTE: Only display messages here. ALL prompts are stored in nodes.
export interface WorkflowMetadata {
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
  // DISPLAY MESSAGES ONLY - not prompts
  welcomeMessage?: string;
  errorMessage?: string;
}

// ===== Workflow Definition (Persisted Format) =====
export interface WorkflowDefinition {
  version: string;
  metadata: WorkflowMetadata;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  viewport: Viewport;
}

// ===== Workflow Database Entity =====
export interface Workflow {
  id: string;
  name: string;
  description?: string;
  definition: WorkflowDefinition;
  version: string;
  isActive: boolean;
  isDefault: boolean;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

// ===== Store Slice Types =====

/**
 * Workspace Slice - Core React Flow state management
 */
export interface WorkspaceSlice {
  // State
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  viewport: Viewport;

  // React Flow Handlers
  onNodesChange: OnNodesChange<WorkflowNode>;
  onEdgesChange: OnEdgesChange;
  onConnect: OnConnect;

  // Node Operations
  setNodes: (nodes: WorkflowNode[]) => void;
  setEdges: (edges: WorkflowEdge[]) => void;
  addNode: (node: WorkflowNode) => void;
  removeNode: (nodeId: string) => void;
  updateNodeData: <T extends WorkflowNodeData>(nodeId: string, data: Partial<T>) => void;
  updateNodePosition: (nodeId: string, position: { x: number; y: number }) => void;

  // Edge Operations
  addEdge: (edge: WorkflowEdge) => void;
  removeEdge: (edgeId: string) => void;

  // Viewport Operations
  setViewport: (viewport: Viewport) => void;

  // Bulk Operations
  clearWorkspace: () => void;
  loadWorkflow: (definition: WorkflowDefinition) => void;
}

/**
 * Selection Slice - Selected elements state
 */
export interface SelectionSlice {
  // State
  selectedNodeId: string | null;
  selectedEdgeId: string | null;
  multiSelectedNodeIds: string[];

  // Actions
  setSelectedNodeId: (nodeId: string | null) => void;
  setSelectedEdgeId: (edgeId: string | null) => void;
  setMultiSelectedNodeIds: (nodeIds: string[]) => void;
  addToMultiSelection: (nodeId: string) => void;
  removeFromMultiSelection: (nodeId: string) => void;
  clearSelection: () => void;
}

/**
 * Palette Slice - UI state for node palette and drag-drop
 */
export interface PaletteSlice {
  // State
  isPaletteOpen: boolean;
  isDragging: boolean;
  draggedNodeType: string | null;

  // Actions
  setIsPaletteOpen: (isOpen: boolean) => void;
  togglePalette: () => void;
  setIsDragging: (isDragging: boolean) => void;
  setDraggedNodeType: (nodeType: string | null) => void;
}

/**
 * Persistence Slice - Save/Load workflow state
 * NOTE: Only display messages are in metadata. All prompts are in nodes.
 */
export interface PersistenceSlice {
  // State
  currentWorkflowId: string | null;
  currentWorkflowName: string;
  currentWorkflowDescription: string;
  isDirty: boolean;
  isSaving: boolean;
  lastSavedAt: string | null;
  // Workflow metadata (DISPLAY MESSAGES ONLY)
  workflowMetadata: Pick<WorkflowMetadata, 'welcomeMessage' | 'errorMessage'>;

  // Actions
  setCurrentWorkflowId: (id: string | null) => void;
  setCurrentWorkflowName: (name: string) => void;
  setCurrentWorkflowDescription: (description: string) => void;
  setIsDirty: (isDirty: boolean) => void;
  setIsSaving: (isSaving: boolean) => void;
  setLastSavedAt: (timestamp: string | null) => void;
  markAsDirty: () => void;
  markAsSaved: () => void;
  // Metadata actions (DISPLAY MESSAGES ONLY)
  setWorkflowMetadata: (metadata: Partial<PersistenceSlice['workflowMetadata']>) => void;
  setWelcomeMessage: (message: string) => void;
  setErrorMessage: (message: string) => void;
}

/**
 * Validation Slice - Workflow validation state
 */
export interface ValidationSlice {
  // State
  validationErrors: ValidationError[];
  isValid: boolean;

  // Actions
  setValidationErrors: (errors: ValidationError[]) => void;
  addValidationError: (error: ValidationError) => void;
  clearValidationErrors: () => void;
  validateWorkflow: () => boolean;
}

export interface ValidationError {
  id: string;
  nodeId?: string;
  edgeId?: string;
  type: 'error' | 'warning';
  message: string;
  code: string;
}

// ===== Combined Store Type =====
export interface WorkflowStore extends WorkspaceSlice, SelectionSlice, PaletteSlice, PersistenceSlice, ValidationSlice {
  // Computed/derived state helpers
  getSelectedNode: () => WorkflowNode | undefined;
  getNodeById: (nodeId: string) => WorkflowNode | undefined;
  getConnectedNodes: (nodeId: string) => WorkflowNode[];
  exportWorkflow: () => WorkflowDefinition;
}

// ===== Node Palette Item =====
export interface NodePaletteItem {
  type: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  category: 'core' | 'system' | 'orchestrator' | 'agent' | 'tool' | 'logic';
  defaultData: Partial<WorkflowNodeData>;
}

// ===== Helper Type Guards =====
export function isOrchestratorNode(data: WorkflowNodeData): data is OrchestratorNodeData {
  return data.type === 'orchestrator';
}

export function isAgentNode(data: WorkflowNodeData): data is AgentNodeData {
  return data.type === 'agent';
}

export function isSystemNode(data: WorkflowNodeData): data is SystemNodeData {
  return data.type === 'system';
}

export function isConditionNode(data: WorkflowNodeData): data is ConditionNodeData {
  return data.type === 'condition';
}

export function hasEditablePrompt(data: WorkflowNodeData): data is OrchestratorNodeData | AgentNodeData {
  return data.type === 'orchestrator' || data.type === 'agent';
}
